import type {
  RespondentMethodology,
  RespondentScreenerQuestion,
} from "./types";
import type { CountryIso3 } from "@/components/outreach/recruitment/panelPrediction/panelRegistry/countries";

export {
  RESPONDENT_FILTER_IDS,
  RESPONDENT_FILTER_SELECTION_CAPS,
  type RespondentFilterId,
} from "./filter_ids";

// Respondent.io's publish endpoint requires the project to carry >=2
// screener questions. Listen Labs studies do their qualification inside
// the AI interview rather than as a separate question set, so every
// project we launch reuses this token screener: a consent radio plus a
// free-text intro. The radio's "Yes" qualifies, so anyone who opts in
// flows through to our screener side, which does the real screening
// work. A Listen-Labs-screener -> Respondent-screener converter is a
// future improvement.
export const RESPONDENT_FALLBACK_SCREENER_QUESTIONS: RespondentScreenerQuestion[] =
  [
    {
      questionType: "radio",
      uid: "f0000000-0000-4000-8000-000000000001",
      text: "Are you available to start a short paid research session today?",
      isRequired: true,
      answers: [
        { text: "Yes, sign me up", answerValue: 1 },
        { text: "No, not right now", answerValue: 2 },
      ],
    },
    {
      questionType: "singleLineTextbox",
      text: "Briefly tell us about yourself.",
    },
  ];

export const RESPONDENT_DEFAULT_METHODOLOGY: RespondentMethodology =
  "unmoderatedStudy";

// Respondent rejects incentiveAmount < 5 (USD).
export const RESPONDENT_MIN_INCENTIVE_DOLLARS = 5;
export const RESPONDENT_INCENTIVE_CURRENCY = "USD";
export const RESPONDENT_LAUNCH_TARGET_MULTIPLIER = 3;
export const RESPONDENT_MAX_TARGET_PARTICIPANTS = 500;
// Service fee Respondent charges us per completed interview.
export const RESPONDENT_SESSION_FEE_B2C_CENTS = 500;
export const RESPONDENT_SESSION_FEE_B2B_CENTS = 1500;

// Respondent rejects participantTimeRequiredMinutes < 5.
export const RESPONDENT_MIN_PARTICIPANT_TIME_MINUTES = 5;

// Respondent rejects publicInternalName longer than 50 characters.
export const RESPONDENT_MAX_INTERNAL_NAME_LENGTH = 50;

export const RESPONDENT_API_CALL_GET_REQUEST_TIMEOUT_MS = 15000;

export const RESPONDENT_PROD_BASE_URL = "https://api.respondent.io";
export const RESPONDENT_STAGING_BASE_URL = "https://api-staging.respondent.io";

// Sent with a screen-out for a full quota, which the participant sees.
export const RESPONDENT_QUOTA_FULL_MESSAGE =
  "Thank you for applying. This study has already found everyone it needs with your background, so we can't invite you this time. Please keep an eye out for future studies.";

// Sent with a screen-out for an invite left unused too long, which the participant sees.
export const RESPONDENT_INVITE_EXPIRED_MESSAGE =
  "Thank you for applying. Your invitation expired before you started, so we have offered the place to someone else. Please keep an eye out for future studies.";

// Respondent rejects publicDescription longer than 800 characters.
export const RESPONDENT_MAX_PUBLIC_DESCRIPTION_LENGTH = 800;

// An invite this old is not counted as holding a place.
export const RESPONDENT_STALE_INVITE_MS = 12 * 60 * 60 * 1000;
// Later than RESPONDENT_STALE_INVITE_MS, so slow invitees keep a fair window.
export const RESPONDENT_CANCEL_STALE_INVITE_MS = 48 * 60 * 60 * 1000;
export const RESPONDENT_INVITE_RESEND_INTERVAL_MS = 3 * 60 * 60 * 1000;
// Every interval through the first day, after the first invite.
export const RESPONDENT_INVITE_RESENDS = 7;
export const RESPONDENT_INVITE_ACTIVITY_ATTEMPTS = 5;
// Respondent answers simultaneous writes to one project with a 409, so a batch's first invites go out one at a time.
export const RESPONDENT_INVITE_SEND_SPACING_MS = 2_000;

// Over-invite, since a share of every batch goes cold too.
export const RESPONDENT_INVITE_HEADROOM = 1.5;
// Interviews take 10 minutes at the median and 17 at p90, so an hour with no new message means the participant left.
export const RESPONDENT_ABANDONED_INTERVIEW_MS = 60 * 60 * 1000;

// Keys are Respondent's exact enum values (sent on the wire); a mismatch is a silent 400 at launch.
export const RESPONDENT_GENDER_CHOICES: Record<string, string> = {
  male: "Male",
  female: "Female",
  nonbinary: "Non-binary",
  transmale: "Trans male",
  transfemale: "Trans female",
  other: "Other",
  prefernottoanswer: "Prefer not to answer",
};

export const RESPONDENT_EDUCATION_CHOICES: Record<string, string> = {
  somehighschool: "Some high school",
  highschoolgraduate: "High school graduate",
  somecollegenodegree: "Some college, no degree",
  tradetechnicalvocationaltraining: "Trade / technical / vocational training",
  associatedegree: "Associate degree",
  bachelordegree: "Bachelor's degree",
  masterdegree: "Master's degree",
  doctoratedegree: "Doctorate degree",
};

export const RESPONDENT_ETHNICITY_CHOICES: Record<string, string> = {
  whitecaucasian: "White / Caucasian",
  blackorafricanamerican: "Black / African American",
  hispaniclatino: "Hispanic / Latino",
  asianpacificislander: "Asian / Pacific Islander",
  americanindianalaskannative: "American Indian / Alaskan Native",
  middleeastern: "Middle Eastern",
  multipleethnicity: "Multiple ethnicity",
  prefernotdisclose: "Prefer not to disclose",
};

export const RESPONDENT_AGE_GROUP_CHOICES: Record<string, string> = {
  "18-20": "18 to 20",
  "21-29": "21 to 29",
  "30-39": "30 to 39",
  "40-54": "40 to 54",
  "55+": "55 and over",
};

export const RESPONDENT_HOUSEHOLD_INCOME_CHOICES: Record<string, string> = {
  "<30000": "Under $30,000",
  "30000-39999": "$30,000 to $39,999",
  "40000-59999": "$40,000 to $59,999",
  "60000-79999": "$60,000 to $79,999",
  "80000-99999": "$80,000 to $99,999",
  "100000-149999": "$100,000 to $149,999",
  "150000-199999": "$150,000 to $199,999",
  "200000+": "$200,000+",
};

export const RESPONDENT_COUNTRY_CHOICES = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  AU: "Australia",
  IE: "Ireland",
  DE: "Germany",
  FR: "France",
  ES: "Spain",
  IT: "Italy",
  NL: "Netherlands",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  CH: "Switzerland",
  AT: "Austria",
  BE: "Belgium",
  NZ: "New Zealand",
  JP: "Japan",
  SG: "Singapore",
  BR: "Brazil",
  MX: "Mexico",
  ID: "Indonesia",
  PH: "Philippines",
  IN: "India",
  EG: "Egypt",
  PL: "Poland",
  PT: "Portugal",
  GR: "Greece",
  CZ: "Czechia",
  HU: "Hungary",
  RO: "Romania",
  BG: "Bulgaria",
  TR: "Turkey",
  IL: "Israel",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  ZA: "South Africa",
  KR: "South Korea",
  HK: "Hong Kong",
  MY: "Malaysia",
  TH: "Thailand",
  VN: "Vietnam",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  PR: "Puerto Rico",
} as const satisfies Record<string, string>;

export const RESPONDENT_ISO2_TO_ISO3 = {
  US: "USA",
  CA: "CAN",
  GB: "GBR",
  AU: "AUS",
  IE: "IRL",
  DE: "DEU",
  FR: "FRA",
  ES: "ESP",
  IT: "ITA",
  NL: "NLD",
  SE: "SWE",
  NO: "NOR",
  DK: "DNK",
  FI: "FIN",
  CH: "CHE",
  AT: "AUT",
  BE: "BEL",
  NZ: "NZL",
  JP: "JPN",
  SG: "SGP",
  BR: "BRA",
  MX: "MEX",
  ID: "IDN",
  PH: "PHL",
  IN: "IND",
  EG: "EGY",
  PL: "POL",
  PT: "PRT",
  GR: "GRC",
  CZ: "CZE",
  HU: "HUN",
  RO: "ROU",
  BG: "BGR",
  TR: "TUR",
  IL: "ISR",
  AE: "ARE",
  SA: "SAU",
  ZA: "ZAF",
  KR: "KOR",
  HK: "HKG",
  MY: "MYS",
  TH: "THA",
  VN: "VNM",
  AR: "ARG",
  CL: "CHL",
  CO: "COL",
  PE: "PER",
  PR: "PRI",
} satisfies Record<keyof typeof RESPONDENT_COUNTRY_CHOICES, CountryIso3>;

export const RESPONDENT_SENIORITY_CHOICES: Record<string, string> = {
  unpaid: "Unpaid",
  student: "Student",
  training: "In training",
  entrylevel: "Entry level",
  senior: "Senior",
  manager: "Manager",
  director: "Director",
  vicepresident: "Vice president",
  chiefofficer: "C-level",
  partner: "Partner",
};

// No "all" choice even though the API enum has one: an empty selection
// already means unconstrained, and "all" mixed with specific sizes is
// incoherent.
export const RESPONDENT_COMPANY_SIZE_CHOICES: Record<string, string> = {
  selfemployed: "Self-employed",
  "1-10": "1 to 10 employees",
  "11-50": "11 to 50 employees",
  "51-200": "51 to 200 employees",
  "201-500": "201 to 500 employees",
  "501-1000": "501 to 1,000 employees",
  "1001-5000": "1,001 to 5,000 employees",
  "5001-10000": "5,001 to 10,000 employees",
  "10000+": "Over 10,000 employees",
};

export const RESPONDENT_JOB_FUNCTION_CHOICES: Record<string, string> = {
  accountingAuditing: "Accounting / Auditing",
  administrative: "Administrative",
  advertising: "Advertising",
  analyst: "Analyst",
  artCreative: "Art / Creative",
  businessDevelopment: "Business development",
  consulting: "Consulting",
  customerService: "Customer service",
  design: "Design",
  distribution: "Distribution",
  education: "Education",
  finance: "Finance",
  generalBusiness: "General business",
  healthcareProvider: "Healthcare provider",
  humanResources: "Human resources",
  informationTechnology: "Information technology",
  legal: "Legal",
  management: "Management",
  manufacturing: "Manufacturing",
  marketing: "Marketing",
  production: "Production",
  productManagement: "Product management",
  projectManagement: "Project management",
  publicRelations: "Public relations",
  purchasing: "Purchasing",
  qualityAssurance: "Quality assurance",
  research: "Research",
  sales: "Sales",
  science: "Science",
  strategyPlanning: "Strategy / Planning",
  supplyChain: "Supply chain",
  training: "Training",
  writingEditing: "Writing / Editing",
  other: "Other",
};

// Query-string params Respondent appends to the redirect URL when
// forwarding a qualified participant. We read them on the screener side;
// we never set them ourselves.
export const RESPONDENT_URL_PARAMS = [
  "respondent_profile_id",
  "respondent_user_id",
  "respondent_project_id",
  "respondent_screener_response_id",
];
